import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { formatDate } from '../../lib/format.js'
import { downloadBlob } from '../../lib/download.js'

/*
 * Company archives (inventory §5 — Archives tab). Request a downloadable archive
 * of the invoices over a period (GET /archives/preview for the count, POST
 * /archives/request), then list/download/delete past requests (/archives/...).
 * Generation is async — the list shows the status and refreshes on demand.
 */
const STATUS_TONE = { completed: 'success', ready: 'success', pending: 'neutral', processing: 'info', failed: 'danger', expired: 'neutral' }

export default function ArchivesTab() {
  const { t } = useTranslation()
  const { confirm, toast, notify } = useFeedback()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/archives/requests').then(({ data }) => setRequests(data?.archive_requests || [])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  // Live preview of how many invoices the period covers.
  useEffect(() => {
    if (!from || !to) { setPreview(null); return undefined }
    const id = setTimeout(() => {
      api.get('/archives/preview', { params: { period_start: `${from}T00:00:00`, period_end: `${to}T23:59:59` } })
        .then(({ data }) => setPreview(data)).catch(() => setPreview(null))
    }, 300)
    return () => clearTimeout(id)
  }, [from, to])

  const request = async () => {
    if (!from || !to) return
    setBusy(true)
    try {
      await api.post('/archives/request', { period_start: `${from}T00:00:00`, period_end: `${to}T23:59:59` })
      toast(t('archives.requested'), 'success'); setFrom(''); setTo(''); setPreview(null); load()
    } catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setBusy(false) }
  }
  const download = (r) => downloadBlob(`/archives/${r.id}/download`, `archive-${r.id}.zip`).catch(() => toast(t('archives.downloadError'), 'error'))
  const remove = async (r) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('archives.confirmDelete'), confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/archives/${r.id}`); toast(t('archives.deleted'), 'success'); load() }
    catch { toast(t('settings.errorGeneric'), 'error') }
  }

  const ready = (r) => r.has_file && (r.status === 'completed' || r.status === 'ready')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--color-bg-lilac-strong)', color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="invoice" size={20} /></span>
          <div>
            <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15 }}>{t('archives.requestTitle')}</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('archives.requestIntro')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
          <Field label={t('archives.from')} style={{ marginBottom: 0, width: 180 }}><Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></Field>
          <div style={{ height: 'var(--field-height)', display: 'flex', alignItems: 'center', color: 'var(--color-text-muted)' }}><Icon name="chevron-right" size={16} /></div>
          <Field label={t('archives.to')} style={{ marginBottom: 0, width: 180 }}><Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></Field>
          <Button variant="primary" icon="download" loading={busy} disabled={!from || !to} onClick={request}>{t('archives.request')}</Button>
          {preview && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 'var(--field-height)', padding: '0 14px', background: 'var(--color-bg-lilac)', borderRadius: 10, fontSize: 13.5, fontWeight: 600, color: 'var(--color-primary)' }}>
              <Icon name="invoice" size={15} /> {t('archives.previewCount', { count: preview.invoice_count })}
            </div>
          )}
        </div>
      </Card>

      <Card padding={0}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border-row)' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15 }}>{t('archives.historyTitle')}</h3>
          <Button variant="secondary" icon="settings" onClick={load}>{t('archives.refresh')}</Button>
        </div>
        {loading ? <div style={{ padding: 28, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
          : requests.length === 0 ? <div style={{ padding: '36px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('archives.empty')}</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
                {[t('archives.period'), t('admin.companies.status'), t('archives.requestedAt'), ''].map((h, i) => (
                  <th key={i} style={{ textAlign: i === 3 ? 'right' : 'left', padding: '11px 20px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                    <td style={{ padding: '12px 20px', color: 'var(--color-ink)', fontSize: 14 }}>{formatDate(r.period_start)} – {formatDate(r.period_end)}</td>
                    <td style={{ padding: '12px 20px' }}><Badge tone={STATUS_TONE[r.status] || 'neutral'}>{t(`archives.status.${r.status}`, r.status)}</Badge>{r.status === 'failed' && r.error_message && <div style={{ fontSize: 12, color: 'var(--color-danger)' }}>{r.error_message}</div>}</td>
                    <td style={{ padding: '12px 20px', color: 'var(--color-text-muted)', fontSize: 13 }}>{formatDate(r.created_at)}</td>
                    <td style={{ padding: '12px 20px', textAlign: 'right' }}><div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
                      {ready(r) && <IconButton icon="download" title={t('archives.download')} onClick={() => download(r)} />}
                      <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => remove(r)} />
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', background: 'var(--color-bg-lilac)', borderRadius: 12, fontSize: 13, color: 'var(--color-text)' }}>
        <span style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: 1 }}><Icon name="info" size={16} /></span>
        <span>{t('archives.notice')}</span>
      </div>
    </div>
  )
}
